'use client'

import { useSyncExternalStore } from 'react'
import { firebaseAuth } from '@/lib/firebase'
import { cleanEquipped, cleanOwned, getPrize, type Equipped, type SkinGame } from '@/lib/prizes'

// Small shared store for the signed-in person's own prizes (owned items + equipped set).
// app/page.tsx feeds it from the live profile; the Prize store screen and the game boards read it.
// Everything here is display state only - ownership is decided by /api/prizes on the server.
type State = { owned: Record<string, number>; equipped: Equipped }
let state: State = { owned: {}, equipped: {} }
const listeners = new Set<() => void>()
const emit = () => listeners.forEach((l) => l())

export function setPrizeState(owned: unknown, equipped: unknown) {
  const next: State = { owned: cleanOwned(owned), equipped: cleanEquipped(equipped) }
  if (JSON.stringify(next) === JSON.stringify(state)) return
  state = next
  emit()
}
export function patchPrizeState(p: { owned?: Record<string, number>; equipped?: Equipped }) {
  state = { owned: p.owned ?? state.owned, equipped: p.equipped ?? state.equipped }
  emit()
}
export const getPrizeState = () => state
export function usePrizeState(): State {
  return useSyncExternalStore((cb) => { listeners.add(cb); return () => { listeners.delete(cb) } }, () => state, () => state)
}
/** The equipped id for any slot ('' = none), e.g. useEquippedSlot('celebration'). */
export function useEquippedSlot(slot: keyof Equipped): string {
  return useSyncExternalStore((cb) => { listeners.add(cb); return () => { listeners.delete(cb) } }, () => state.equipped[slot] || '', () => '')
}
/** The equipped skin id for one game ('' = none). Primitive snapshot, so a game board only re-renders when its own skin changes. */
export function useEquippedSkin(game: SkinGame): string {
  const key = `skin_${game}` as keyof Equipped
  return useSyncExternalStore((cb) => { listeners.add(cb); return () => { listeners.delete(cb) } }, () => state.equipped[key] || '', () => '')
}

const ERR: Record<string, string> = {
  'not-enough-coins': 'Not enough coins.',
  'already-owned': 'You already own this.',
  'not-owned': 'You do not own this yet.',
  'not-for-sale': 'This one is not for sale - get it from a Lucky Box or by winning games.',
  'no-account': 'Please sign in again.',
  unauthenticated: 'Please sign in again.',
}

export type PrizeResult = { ok: true; [k: string]: any } | { ok: false; message: string }

/** Calls /api/prizes with the signed-in user's token. Never throws. */
export async function prizeApi(body: Record<string, unknown>): Promise<PrizeResult> {
  try {
    const token = await firebaseAuth.currentUser?.getIdToken()
    if (!token) return { ok: false, message: 'Please sign in again.' }
    const res = await fetch('/api/prizes', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(body),
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok || data.error) {
      return { ok: false, message: ERR[data.error] || (res.status === 429 ? 'Too many tries. Wait a moment.' : 'Could not reach the server. Try again.') }
    }
    return { ok: true, ...data }
  } catch {
    return { ok: false, message: 'Could not reach the server. Try again.' }
  }
}

/** Collects prizes earned by winning online games. Returns the items that were just added (empty = nothing new). */
export async function claimPrizeDrops(): Promise<{ id: string; duplicate: boolean; refund: number }[]> {
  return [] // win drops are off: store items are coin-only
  // eslint-disable-next-line no-unreachable
  const r = await prizeApi({ action: 'claim_drops' })
  if (!r.ok) return []
  const items = ((r as { items?: unknown }).items || []) as { id: string; duplicate: boolean; refund: number }[]
  const fresh = items.filter((i) => getPrize(i.id))
  if (fresh.some((i) => !i.duplicate)) patchPrizeState({ owned: { ...state.owned, ...Object.fromEntries(fresh.filter((i) => !i.duplicate).map((i) => [i.id, Date.now()])) } })
  return fresh
}
